import { useAccountEventsStore } from '@/stores/account-events'

// Events that change the flags, targets, rules, values, or rollouts a report
// shows. Evaluation and usage events are left out on purpose: they fire on
// every flag read and would keep every report permanently out of date.
export const PROJECT_CHANGE_EVENTS = [
    'flag-stored',
    'flag-updated',
    'flag-destroyed',
    'flag-value-stored',
    'flag-value-updated',
    'flag-value-destroyed',
    'target-stored',
    'target-updated',
    'target-destroyed',
    'target-rule-stored',
    'target-rule-updated',
    'target-rule-destroyed',
    'target-rule-reordered',
    'target-rollout-updated',
]

/**
 * Call `onChange` when an event changes the project returned by
 * `getProjectKey`, and `onResync` when the account connection comes back from
 * a drop. Events for other projects are ignored.
 *
 * @param {object} options
 * @param {() => string|undefined} options.getProjectKey Read at event time, so
 *   the check always uses the project the screen shows now.
 * @param {(eventName: string) => void} options.onChange
 * @param {() => void} options.onResync
 * @param {string[]} [options.eventNames=PROJECT_CHANGE_EVENTS]
 * @returns {() => void} Stops listening.
 */
export function listenForProjectChanges({
    getProjectKey,
    onChange,
    onResync,
    eventNames = PROJECT_CHANGE_EVENTS,
}) {
    const accountEvents = useAccountEventsStore()

    const stops = eventNames.map((eventName) =>
        accountEvents.on(eventName, (data) => {
            const projectKey = getProjectKey()
            if (!projectKey || data?.projectKey !== projectKey) return

            onChange(eventName)
        })
    )
    stops.push(accountEvents.onResync(onResync))

    return () => stops.forEach((stop) => stop())
}
